import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { JsonInput } from '@/components/form-components/JsonInput'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'

import { useCurrentApplication } from '../useCurrentApplication'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

type AppTypes = RestSchemas['Common.AppTypes']
type MessagesPipelinesExistingPipeline = RestSchemas['Messages.Pipeline.ExistingPipeline']
type MessagesPipelinesNewPipeline = RestSchemas['Messages.Pipeline.NewPipeline']

const PIPELINE_TYPE_OPTIONS = [
  { label: 'Databricks', value: '1' },
  { label: 'Dagster', value: '2' },
  { label: 'Jupyter Hub', value: '4' },
]

const DATABRICKS_TYPE_VALUE = '1'

const pipelineFormBaseSchema = z.object({
  name: z.string().max(60, 'Name cannot exceed 60 characters').optional(),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional(),
  type: z.string().optional(),
  definition: z.string().optional(),
  databricksNotebookId: z.string().optional(),
})

type TPipelineForm = z.infer<typeof pipelineFormBaseSchema>

const createPipelineFormSchema = (mode: 'pipeline' | 'definition') =>
  pipelineFormBaseSchema.superRefine((data, ctx) => {
    if (mode === 'pipeline') {
      if (!data.name || data.name.trim().length === 0) {
        ctx.addIssue({ path: ['name'], code: z.ZodIssueCode.custom, message: 'Name is required' })
      }
      if (!data.type || data.type.trim().length === 0) {
        ctx.addIssue({ path: ['type'], code: z.ZodIssueCode.custom, message: 'Type is required' })
      } else if (!PIPELINE_TYPE_OPTIONS.some((option) => option.value === data.type)) {
        ctx.addIssue({ path: ['type'], code: z.ZodIssueCode.custom, message: 'Invalid pipeline type selected' })
      }
      if (data.type === DATABRICKS_TYPE_VALUE && (!data.databricksNotebookId || data.databricksNotebookId.trim().length === 0)) {
        ctx.addIssue({ path: ['databricksNotebookId'], code: z.ZodIssueCode.custom, message: 'Notebook is required for Databricks pipelines' })
      }
    } else {
      const definition = data.definition ?? ''
      if (definition.trim().length === 0) {
        ctx.addIssue({ path: ['definition'], code: z.ZodIssueCode.custom, message: 'Definition is required' })
      } else {
        try {
          JSON.parse(definition)
        } catch (_err) {
          ctx.addIssue({ path: ['definition'], code: z.ZodIssueCode.custom, message: 'Definition must be valid JSON' })
        }
      }
    }
  })

type AddPipelineFormV2Props = {
  mode: 'pipeline' | 'definition'
  onSubmit?: () => void
  sheetTrigger?: ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedPipeline?: MessagesPipelinesExistingPipeline
  applicationId?: number
}

export const AddPipelineFormV2 = (props: AddPipelineFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedPipeline
  const { applicationId: currentApplicationId } = useCurrentApplication()
  const applicationId = props.applicationId ?? props.selectedPipeline?.applicationId ?? currentApplicationId

  const { putPipelineApiAddPipeline, postPipelineApiPostPipeline, getPipelineApiGetPipeline } = useRestClient()

  const invalidatePipelines = useCallback(() => {
    if (!applicationId) return
    getPipelineApiGetPipeline.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getPipelineApiGetPipeline])

  const { mutate: updatePipeline, isPending: isUpdating } = postPipelineApiPostPipeline.useMutation(undefined, {
    onSuccess: () => {
      invalidatePipelines()
      if (props.sheetTrigger || props.openInSheet) {
        setSheetOpen(false)
      }
      toast.success('Pipeline successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating pipeline')
    },
  })

  const { mutate: addPipeline, isPending: isAdding } = putPipelineApiAddPipeline.useMutation(undefined, {
    onSuccess: () => {
      invalidatePipelines()
      if (props.sheetTrigger || props.openInSheet) {
        setSheetOpen(false)
      }
      toast.success('Pipeline successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding pipeline')
    },
  })

  const submitHandler = useCallback(
    (data: TPipelineForm) => {
      if (!applicationId) {
        toast.error('Application context not found')
        return
      }

      const nameFromForm = data.name?.trim() ?? ''
      const descriptionFromForm = data.description?.trim() ?? ''
      const typeFromForm = data.type ?? ''
      const parsedType = typeFromForm ? parseInt(typeFromForm, 10) : undefined
      const pipelineType = Number.isNaN(parsedType) ? undefined : (parsedType as AppTypes)

      const resolvedName = nameFromForm || props.selectedPipeline?.name || ''
      const resolvedDescription =
        descriptionFromForm !== '' || props.mode === 'pipeline'
          ? descriptionFromForm
          : props.selectedPipeline?.description || ''

      const resolvedDefinition = data.definition?.trim() || props.selectedPipeline?.definition || '{}'

      if (edit && props.selectedPipeline) {
        const resolvedType = pipelineType ?? props.selectedPipeline.type
        const updatedPipeline: MessagesPipelinesExistingPipeline = {
          id: props.selectedPipeline.id,
          applicationId,
          type: resolvedType,
          name: resolvedName || props.selectedPipeline.name,
          description: resolvedDescription,
          definition: resolvedDefinition,
        }

        updatePipeline({ path: { applicationId }, body: updatedPipeline })
      } else {
        if (!resolvedName) {
          toast.error('Pipeline name is required')
          return
        }

        if (!pipelineType) {
          toast.error('Pipeline type is required')
          return
        }

        const newPipeline: MessagesPipelinesNewPipeline = {
          applicationId,
          type: pipelineType,
          name: resolvedName,
          description: resolvedDescription,
          definition: resolvedDefinition,
        }

        addPipeline({ path: { applicationId }, body: newPipeline })
      }
    },
    [addPipeline, applicationId, edit, props.mode, props.selectedPipeline, updatePipeline]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  const defaultTitle =
    props.sheetTitle ||
    (edit
      ? props.mode === 'definition'
        ? 'Edit Pipeline Definition'
        : 'Edit Pipeline'
      : props.mode === 'definition'
        ? 'Save Pipeline Definition'
        : 'Create Pipeline')

  const defaultDescription =
    props.sheetDescription ||
    (edit
      ? props.mode === 'definition'
        ? 'Update the JSON definition for this pipeline'
        : 'Update the pipeline details'
      : props.mode === 'definition'
        ? 'Provide the JSON definition for this pipeline'
        : 'Create a new pipeline for your application')

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={defaultTitle}
      description={defaultDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} applicationId={applicationId} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} applicationId={applicationId} submitHandler={submitHandler} />
  )
}

function parseDatabricksDefinition(definition?: string | null): string {
  if (!definition) return ''
  try {
    const parsed = JSON.parse(definition)
    return parsed.databricksNotebookId != null ? String(parsed.databricksNotebookId) : ''
  } catch {
    return ''
  }
}

const FormComponent = (
  props: AddPipelineFormV2Props & {
    applicationId: number
    submitHandler: (data: TPipelineForm) => void
  }
) => {
  const edit = !!props.selectedPipeline
  const { getDatabricksApiGetDatabricksApplicationStatus } = useRestClient()

  const defaultValues = useMemo(() => {
    const pipeline = props.selectedPipeline
    return {
      name: pipeline?.name ?? '',
      description: pipeline?.description ?? '',
      type: pipeline ? String(pipeline.type) : '',
      definition: props.mode === 'definition' ? (pipeline?.definition ?? '') : (pipeline?.definition ?? '{}'),
      databricksNotebookId:
        pipeline && String(pipeline.type) === DATABRICKS_TYPE_VALUE
          ? parseDatabricksDefinition(pipeline.definition)
          : '',
    }
  }, [props.mode, props.selectedPipeline])

  const schema = useMemo(() => createPipelineFormSchema(props.mode), [props.mode])

  const form = useForm<TPipelineForm>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const selectedType = form.watch('type')
  const isDatabricks = selectedType === DATABRICKS_TYPE_VALUE

  const { data: applicationStatus, isLoading: isLoadingNotebooks } =
    getDatabricksApiGetDatabricksApplicationStatus.useQuery(
      { query: { applicationId: props.applicationId } },
      { enabled: isDatabricks && !!props.applicationId }
    )
  const notebooks = useMemo(() => applicationStatus?.notebooks ?? [], [applicationStatus?.notebooks])
  // productionNotebooks is present in the backend DTO but the schema is auto-generated; cast to access it
  const productionNotebooks = useMemo(
    () =>
      ((applicationStatus as Record<string, unknown>)?.productionNotebooks as typeof notebooks | undefined) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(applicationStatus as Record<string, unknown>)?.productionNotebooks]
  )

  const { submitHandler: parentSubmitHandler } = props
  const wrappedSubmitHandler = useCallback(
    (data: TPipelineForm) => {
      if (data.type === DATABRICKS_TYPE_VALUE && data.databricksNotebookId) {
        const selected = notebooks.find((nb) => String(nb.objectId) === data.databricksNotebookId)
        const matchingProd = productionNotebooks.find(
          (nb: (typeof notebooks)[number]) => nb.notebookPath === selected?.notebookPath
        )
        data.definition = JSON.stringify({
          databricksNotebookId: selected?.objectId ?? Number(data.databricksNotebookId),
          databricksNotebookPath: selected?.notebookPath ?? '',
          databricksProductionNotebookId: matchingProd?.objectId ?? null,
        })
      }
      parentSubmitHandler(data)
    },
    [notebooks, productionNotebooks, parentSubmitHandler]
  )

  const isPipelineMode = props.mode === 'pipeline'

  const submitButtonLabel = edit
    ? props.mode === 'definition'
      ? 'Update Definition'
      : 'Update Pipeline'
    : props.mode === 'definition'
      ? 'Save Definition'
      : 'Create Pipeline'

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(wrappedSubmitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {isPipelineMode && (
                <>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter pipeline name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Describe the pipeline (optional)" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select pipeline type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PIPELINE_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {isDatabricks && (
                    <FormField
                      control={form.control}
                      name="databricksNotebookId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notebook *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger disabled={isLoadingNotebooks}>
                                <SelectValue
                                  placeholder={isLoadingNotebooks ? 'Loading notebooks...' : 'Select a notebook'}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {notebooks.map((nb) => (
                                <SelectItem key={nb.objectId} value={String(nb.objectId)}>
                                  {nb.notebookPath}
                                </SelectItem>
                              ))}
                              {!isLoadingNotebooks && notebooks.length === 0 && (
                                <SelectItem value="_empty" disabled>
                                  No notebooks found
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              {props.mode === 'definition' && (
                <FormField
                  control={form.control}
                  name="definition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Definition (JSON)</FormLabel>
                      <FormControl>
                        <JsonInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="form-action-container mt-6">
              <Button type="submit">{submitButtonLabel}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}

export default AddPipelineFormV2
